import { Button } from '@dapp/multi-lib/sdaoui/Button';
import { Typography } from '@dapp/multi-lib/sdaoui/Typography';
import { StakingPool, StakingProject } from 'api/StakingAPI';
import { usePathname, useRouter } from 'next/navigation';
import React, { ReactNode, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { setSREP } from 'store/features/staking/stakingSlice';
import { StakingCountdown } from 'structure/staking/components/StakingCountdown';
import { StakingPoolActions } from 'structure/staking/components/StakingPoolActions';
import { StakingPoolIcon } from 'structure/staking/components/StakingPoolIcon';
import { StakingPoolTVL } from 'structure/staking/components/StakingPoolTVL';
import { UserClaimable } from 'structure/staking/components/UserClaimable';
import { UserReputation } from 'structure/staking/components/UserReputation';
import { UserStaked } from 'structure/staking/components/UserStaked';
import {
  useStakingPoolData,
  useStakingUserPoolInfo,
} from 'structure/staking/hooks/useStakingAPI';
import { ModalName } from 'structure/staking/types';
import { isUserStaked } from 'structure/staking/utils/userPollInfo.utils';
import { unixTimestampToMs } from 'utils/formateDate';
import { useChainBlock } from 'utils/hooks/useChainBlock';
import { useConnection } from 'wagmi';
import { parseSREP } from '../utils/parseSREP';
import { twMerge } from 'tailwind-merge';
import { Spinner } from 'baseui/spinner';
import { shouldHighlightAPR } from 'structure/staking/utils/stakingValueHighlight';

type StakingPoolListProps = {
  pool: StakingPool;
  project?: StakingProject;
  onOpenModal?: (modalName: ModalName, poolId: number) => void;
  enableExplore?: boolean;
};

interface ICell {
  children: ReactNode;
  className: string;
}

const Cell = ({ children, className }: ICell) => {
  return (
    <td
      className={twMerge(
        'bg-[#191A1E] px-4 py-2 text-sm first:rounded-l-[10px] last:rounded-r-[10px]',
        className
      )}
    >
      {children}
    </td>
  );
};

export function StakingPoolTableRow({
  pool: initialPool,
  onOpenModal,
  enableExplore,
  project,
}: StakingPoolListProps) {
  const { t } = useTranslation('common');
  const account = useConnection();
  const { data: chainBlock } = useChainBlock();
  const router = useRouter();
  const pathname = usePathname();
  const dispatch = useDispatch();
  const stakingPoolData = useStakingPoolData(initialPool);

  const pool = stakingPoolData.data;
  const stakingUserPoolInfo = useStakingUserPoolInfo({
    poolContractAddress: pool?.stakingContractAddress,
    walletAddress: account.address as `0x${string}`,
    chainId: pool?.chainId,
    pool,
  });

  useEffect(() => {
    if (stakingUserPoolInfo.isFetched) {
      const srep = parseSREP(
        stakingUserPoolInfo?.data?.reputationScore || '0'
      ).toNumber();
      dispatch(setSREP(srep));
    }
  }, [stakingUserPoolInfo]);

  const handleOpenModal = (modalName: ModalName, poolId: number) => {
    onOpenModal?.(modalName, poolId);
    stakingUserPoolInfo.refetch();
  };

  const redirectToProject = () =>
    router.push(`${pathname}/${pool?.projectToken}`);

  const staked = isUserStaked(stakingUserPoolInfo.data);

  const cellClassName = staked
    ? 'border border-r-0 border-l-0 border-[#191A1E] first:border-l-[#54FAAA] first:border-l last:border-r-[#54FAAA] last:border-r border-t-[#54FAAA] border-b-[#54FAAA]'
    : '';

  const averageAPR = useMemo(() => {
    if (pool?.depositTokenSymbol === 'SDAO') {
      return '0%';
    }

    if (Number.isFinite(Number(pool?.averageAPR))) {
      return `${pool?.averageAPR}%`;
    }
    return '-';
  }, [pool]);

  const myAPR = useMemo(() => {
    if (pool?.depositTokenSymbol === 'SDAO') {
      return '0%';
    }

    if (stakingUserPoolInfo.isFetched) {
      if (Number.isFinite(Number(stakingUserPoolInfo.data?.apr))) {
        return `${parseFloat(stakingUserPoolInfo.data?.apr || '0').toFixed(2)}%`;
      }
    }
  }, [stakingUserPoolInfo, pool]);

  const formattedAverageAPR = averageAPR.endsWith('%')
    ? averageAPR.slice(0, -1)
    : averageAPR;
  const formattedMyAPR = (myAPR || '0%').slice(0, -1);

  const isStakingPoolDataLoading = useMemo(
    () => stakingPoolData.isLoading || !stakingPoolData.isFetched,
    [stakingPoolData]
  );
  const isStakingUserPoolInfoLoading = useMemo(
    () =>
      isStakingPoolDataLoading ||
      stakingUserPoolInfo.isLoading ||
      !stakingUserPoolInfo.isFetched,
    [stakingUserPoolInfo, isStakingPoolDataLoading]
  );

  return (
    <tr key={initialPool?.id}>
      <Cell className={twMerge(cellClassName)}>
        <StakingPoolIcon project={project} pool={initialPool} />
      </Cell>
      <Cell className={twMerge(cellClassName)}>
        {isStakingPoolDataLoading ? (
          <Spinner $size='14px' $borderWidth='3px' className='mx-auto' />
        ) : (
          <StakingPoolTVL pool={pool} />
        )}
      </Cell>
      <Cell className={twMerge(cellClassName, 'text-center')}>
        {isStakingPoolDataLoading ? (
          <Spinner $size='14px' $borderWidth='3px' className='mx-auto' />
        ) : (
          <Typography
            className={twMerge(
              'pt-px text-xs',
              shouldHighlightAPR(formattedAverageAPR)
                ? 'text-green'
                : 'text-[#B4BCD0]'
            )}
          >
            {averageAPR}
          </Typography>
        )}
      </Cell>
      <Cell className={twMerge(cellClassName, 'text-center')}>
        {isStakingUserPoolInfoLoading ? (
          <Spinner $size='14px' $borderWidth='3px' className='mx-auto' />
        ) : (
          <UserStaked pool={pool} userPoolInfo={stakingUserPoolInfo.data} />
        )}
      </Cell>
      <Cell className={twMerge(cellClassName, 'text-center')}>
        {isStakingUserPoolInfoLoading ? (
          <Spinner $size='14px' $borderWidth='3px' className='mx-auto' />
        ) : (
          <UserClaimable pool={pool} userPoolInfo={stakingUserPoolInfo.data} />
        )}
      </Cell>
      <Cell className={twMerge(cellClassName, 'text-center')}>
        {isStakingUserPoolInfoLoading ? (
          <Spinner $size='14px' $borderWidth='3px' className='mx-auto' />
        ) : (
          <Typography
            className={twMerge(
              'pt-px text-xs',
              shouldHighlightAPR(formattedMyAPR)
                ? 'text-green'
                : 'text-[#B4BCD0]'
            )}
          >
            {myAPR || '0%'}
          </Typography>
        )}
      </Cell>
      <Cell className={twMerge(cellClassName, 'text-center')}>
        {isStakingUserPoolInfoLoading ? (
          <Spinner $size='14px' $borderWidth='3px' className='mx-auto' />
        ) : (
          <UserReputation userPoolInfo={stakingUserPoolInfo.data} />
        )}
      </Cell>
      <Cell className={twMerge(cellClassName, 'text-center')}>
        {isStakingUserPoolInfoLoading ? (
          <Spinner $size='14px' $borderWidth='3px' className='mx-auto' />
        ) : (
          <div className='flex items-center justify-center'>
            <StakingCountdown
              id={initialPool?.stakingContractAddress}
              date={unixTimestampToMs(
                parseInt(stakingUserPoolInfo.data?.unlockDate || '0')
              )}
              timestamp={chainBlock.timestamp}
            />
          </div>
        )}
      </Cell>
      <Cell className={twMerge(cellClassName)}>
        {enableExplore ? (
          <div className='flex w-full flex-row justify-end'>
            <Button onClick={redirectToProject}>
              {t('DYNAVAULT.EXPLORE')}
            </Button>
          </div>
        ) : (
          <StakingPoolActions
            pool={initialPool}
            userPoolInfo={stakingUserPoolInfo.data}
            onOpenModal={handleOpenModal}
          />
        )}
      </Cell>
    </tr>
  );
}
